<template>
  <div class="d-times">
    <div class="row month-selector align-items-center">
      <div class="col d-flex align-items-center">
        <BDropdown
            class="month-drop"
            :text="moment(model).format('MMMM YYYY')"
        >
          <BDropdownItem
              v-for="(m, i) in selectableMonths"
              :key="`month-${i}`"
              @click="() => selectFirstAvailableDateInMonth(m)"
          >
            <span class="d-flex month-item justify-content-between align-center gap-3">
              {{ moment(m).format('MMMM YYYY') }}
              <BBadge :style="{
              'background': buttonBG + '!important',
              'color': buttonText + '!important' }" variant="secondary" pill>{{ daysWithActiveSessionsForMonth(m) }}</BBadge>
            </span>
          </BDropdownItem>
        </BDropdown>
      </div>
      <div class="col-auto month-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#month">
        <i class="fa-sharp fa-regular fa-calendar-range"></i>
      </div>
    </div>
    <div class="dates-scroll-wrapper collapse show gap-3" id="month">
      <SessionDate
          v-for="(ts, i) in sortedSelectableDates"
          :key="`date-${i}-${ts}`"
          :date="new Date(ts)"
          :data-selected="moment(model).isSame(new Date(ts), 'date')"
          :selected="moment(model).isSame(new Date(ts), 'date')"
          :dayBG="buttonBG"
          :dayText="buttonText"
          :ref="moment(model).isSame(new Date(ts), 'date') ? 'selectedDate' : undefined"
          role="button"
          @click="() => { const d = new Date(ts); model = d; setSelectedSession(null); emit('date-changed', d); }"
      />
    </div>
    <hr/>
    <div class="d-flex justify-content-between align-items-center" :class="[enableHideSoldOutCheckbox ? 'mb-3' : 'mb-4']">
      <p class="m-0 fw-bold">Select Time</p>
      <BTabs nav-class="nav-fill" pills nav-wrapper-class="" active-nav-item-class="bg-secondary" nav-item-class="">
        <BTab :title="SessionTimeConstraint.ALL" @click="selectedTimeConstraint = SessionTimeConstraint.ALL"></BTab>
        <BTab :title="SessionTimeConstraint.AM" @click="selectedTimeConstraint = SessionTimeConstraint.AM"></BTab>
        <BTab :title="SessionTimeConstraint.PM" @click="selectedTimeConstraint = SessionTimeConstraint.PM"></BTab>
      </BTabs>
    </div>
    <div v-if="enableHideSoldOutCheckbox">
      <label class="hide-sold-out d-flex align-items-center" for="checkbox">
        <input type="checkbox" id="checkbox" v-model="hideSoldOutCheckbox" class="d-none" />
        <span
            class="custom-checkbox-label"
            :class="{ 'checked': hideSoldOutCheckbox }"
        >
          <i v-if="hideSoldOutCheckbox" class="fa-regular fa-check"></i>
        </span>
        <span class="ms-2">Hide sold out times</span>
      </label>
      <hr class="pb-3 custom-hr">
    </div>
    <div class="sessions-container">
      <div v-if="sessionsForDate.length" class="container px-0">
        <div class="row g-3">
          <div
              v-for="(s, i) in sessionsForDate"
              :key="`time-${i}-${sessionStartTime(s).getTime()}`"
              class="col-4 mt-0 mb-3"
          >
            <div v-if="loadingAvailability" class="text-center py-4">
              <i class="fa fa-spinner fa-spin"></i> Loading sessions...
            </div>
            <div v-else-if="sessionsForDate.length" class="container px-0">
              <SessionTime
                  :session="s"
                  :spaces-available="effectiveSpaces(s)"
                  :selected="selectedSession?.sessionID === s.sessionID"
                  @session-chosen="setSelectedSession(s)"
                  :low-capacity-threshold="props.lowCapacityThreshold"
                  :timeBG="buttonBG"
                  :sale-cutoff-options="props.saleCutoffOptions"
                  :custom-sold-out-threshold="customSoldOutThreshold"
                  :show-session-availability="showSessionAvailability"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="no-sessions-message d-flex">
        <div>
          <i class="fa-solid fa-circle-exclamation me-2"></i>
        </div>
        <div>
          <span>No suitable sessions found.</span>
          <p class="mb-1">Please modify your search criteria.</p>
        </div>
      </div>
    </div>
    <!--    HIDE TICKET PRICING UNTIL IT IS FIXED/REVAMPED -->
    <!--    <div v-if="selectedSession">-->
    <!--      <hr/>-->
    <!--      <TicketPricing :event="event" :tickets="tickets"/>-->
    <!--    </div>-->
  </div>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, watch} from 'vue';
import moment from "moment/moment";
import {sessionStartTime} from "../functions/sessionFunctions";
import {SessionTimeConstraint} from "../types/sessionTimeConstraint";
import TicketPricing from "./TicketPricing.vue";
import SessionDate from "./SessionDate.vue";
import SessionTime from "./SessionTime.vue";
import EventCard from "./EventCard.vue";

const model = defineModel();
const selectedDate = ref([]);
const selectedSession = ref(null);
const selectedTimeConstraint = ref(SessionTimeConstraint.ALL);
const hideSoldOutCheckbox = ref(false);
const enableHideSoldOutCheckbox = ref(true);

// Automatically scroll the selected date into view on first mount
onMounted(() => {
  nextTick(() => {
    scrollToSelectedDate();
  });
});

watch(model, () => {
  nextTick(() => {
    scrollToSelectedDate();
  });
});

const scrollToSelectedDate = () => {
  const selectedElement = document.querySelector('.dates-scroll-wrapper [data-selected="true"]');
  if (selectedElement) {
    selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }
};

const emit = defineEmits(['sessionChosen', 'date-changed']);

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  minDate: {
    type: Date,
    required: true,
  },
  maxDate: {
    type: Date,
    required: true,
  },
  buttonBG: {
    type: String,
    required: true,
  },
  buttonText: {
    type: String,
    required: true,
  },
  lowCapacityThreshold: {
    type: Number,
    default: null,
    required: false,
  },
  tickets: {
    type: Array,
    required: true,
  },
  sessionAvailabilityMap: {
    type: Object,
    default: () => ({}),
  },
  loadingAvailability: Boolean,
  customSoldOutThreshold: {
    type: Number,
    default: null,
    required: false,
  },
  saleCutoffOptions: {
    type: Object,
    required: false,
    default: () => ({
      behaviour: 'do_not_show',
      message: null,
      phone: null,
    }),
  },
  showSessionAvailability: {
    type: Boolean,
    default: false
  }
});


const sessionsForDate = computed(() => {
  const soldOutThreshold = isNaN(Number(props.customSoldOutThreshold)) ? 0 : Number(props.customSoldOutThreshold);
  const sessions = props.event.allSessions
      .filter((s) => moment(s['date']).isSame(moment(model.value), 'date'))
      .filter((s) => props.event.allowSalesDuringSessions || moment(sessionStartTime(s)).isAfter(moment()))
      .filter((s) => {
        if (selectedTimeConstraint.value === SessionTimeConstraint.AM) {
          return moment(sessionStartTime(s)).isSameOrBefore(moment(model.value).set({
            hour: 12,
            minute: 0,
            second: 0
          }));
        }
        if (selectedTimeConstraint.value === SessionTimeConstraint.PM) {
          return moment(sessionStartTime(s)).isSameOrAfter(moment(model.value).set({
            hour: 12,
            minute: 0,
            second: 0
          }));
        }
        return true;
      })
      .map((s) => {
        const apiSpaces = props.sessionAvailabilityMap?.[s.sessionID]?.salesLimitSpacesAvailable;
        const adjusted = typeof apiSpaces === 'number' ? Math.min(s.spacesAvailable, apiSpaces) : s.spacesAvailable;
        if (s.__adjustedSpaces !== adjusted) {
          s.__adjustedSpaces = adjusted;
        }
        return s;
      });

  // Enable/disable Hide Sold Out Checkbox per date
  enableHideSoldOutCheckbox.value = sessions.some((s) => {
    const finalSpaces = s.__adjustedSpaces ?? s.spacesAvailable;
    return finalSpaces <= soldOutThreshold;
  });

  const filtered = sessions.filter((s) => {
    const finalSpaces = s.__adjustedSpaces ?? s.spacesAvailable;
    return !hideSoldOutCheckbox.value || finalSpaces > soldOutThreshold;
  });
  return filtered;
});

const selectableDates = computed(() => {
  // Sessions in the visible month that are eligible
  const eligible = props.event.allSessions
      .filter((s) => moment(s.date).isSame(moment(model.value), 'month'))
      .filter((s) => {
        // Filter replicated isDateEligible() function but without AM/PM filter as this caused them to disappear
        const soldOutThreshold = isNaN(Number(props.customSoldOutThreshold)) ? 0 : Number(props.customSoldOutThreshold);
        if (!props.event.allowSalesDuringSessions && !moment(sessionStartTime(s)).isAfter(moment())) {
          return false;
        }
        return effectiveSpaces(s) > soldOutThreshold;
      });

  // Unique normalised dates for the strip
  const ts = Array.from(
      new Set(
          eligible.map((s) => {
            const d = new Date(s.date);
            d.setHours(0, 0, 0, 0);
            return d.getTime();
          })
      )
  ).sort((a, b) => a - b);

  return ts;
});

const sortedSelectableDates = computed(() => {
  return [...selectableDates.value].sort((a, b) => a - b);
});

const selectableMonths = computed(() => {
  // Fix for months with Daylight Savings getting listed twice in month dropdown
  // Normalise month-start timestamps to numbers
  const minTs = moment(props.minDate).startOf('month').valueOf();
  const maxTs = moment(props.maxDate).startOf('month').valueOf();
  const monthTs = props.event.allSessions.map(s =>
      moment(s.date).startOf('month').valueOf()
  );
  // De-duplicate, sort
  const clamped = monthTs.map(ts => Math.max(minTs, Math.min(ts, maxTs)));
  const unique = Array.from(new Set(clamped)).sort((a, b) => a - b);

  // Convert back to Date objects for the UI/formatting
  return unique.map(ts => new Date(ts));
});

const setSelectedSession = (session) => {
  emit('sessionChosen', session);
  selectedSession.value = session;
}

const daysWithActiveSessionsMap = computed(() => {
  const soldOutThreshold = isNaN(Number(props.customSoldOutThreshold))
      ? 0
      : Number(props.customSoldOutThreshold);

  const map = {};

  props.event.allSessions.forEach((s) => {
    const monthTs = moment(s.date).startOf('month').valueOf();

    if (!props.event.allowSalesDuringSessions && !moment(sessionStartTime(s)).isAfter(moment())) {
      return;
    }

    if (effectiveSpaces(s) <= soldOutThreshold) {
      return;
    }

    const d = new Date(s.date);
    d.setHours(0, 0, 0, 0);
    const dayTs = d.getTime();

    if (!map[monthTs]) {
      map[monthTs] = new Set();
    }
    map[monthTs].add(dayTs);
  });

  const result = {};
  for (const [monthTs, daySet] of Object.entries(map)) {
    result[monthTs] = daySet.size;
  }

  return result;
});

// New lookup wrapper so template remains unchanged
const daysWithActiveSessionsForMonth = (monthDate) => {
  const monthTs = moment(monthDate).startOf('month').valueOf();
  return daysWithActiveSessionsMap.value[monthTs] ?? 0;
};


// Ensure month dropdown selects the first date with a session
const selectFirstAvailableDateInMonth = (monthDate) => {
  const availableDates = selectableDatesForMonth(monthDate);
  const firstAvailableTs = availableDates[0];

  if (firstAvailableTs) {
    const sessionDate = new Date(firstAvailableTs);
    model.value = sessionDate;
    setSelectedSession(null);
    emit('date-changed', sessionDate);
  } else {
    console.warn('No available session dates found for selected month.');
  }
};

// Helper function for above function
const selectableDatesForMonth = (monthDate) => {
  const soldOutThreshold = isNaN(Number(props.customSoldOutThreshold)) ? 0 : Number(props.customSoldOutThreshold);

  return Array.from(
      new Set(
          props.event.allSessions
              .filter((s) => moment(s.date).isSame(moment(monthDate), 'month'))
              .filter((s) => {
                if (!props.event.allowSalesDuringSessions && !moment(sessionStartTime(s)).isAfter(moment())) {
                  return false;
                }

                return effectiveSpaces(s) > soldOutThreshold;
              })
              .map((s) => {
                const d = new Date(s.date);
                d.setHours(0, 0, 0, 0);
                return d.getTime();
              })
      )
  ).sort((a, b) => a - b);
};

////////////////////////////
// Helper functions
////////////////////////////

//  Helpers to keep eligibility logic consistent across month badge and Session Date component
const effectiveSpaces = (s) => {
  const apiSpaces = props.sessionAvailabilityMap?.[s.sessionID]?.salesLimitSpacesAvailable;
  const base = Number(s.spacesAvailable);
  return typeof apiSpaces === 'number' ? Math.min(base, apiSpaces) : base;
};

</script>

<style scoped lang="scss">
.dates-scroll-wrapper {
  overflow-x: scroll;
  margin-top: 15px;

  &.show {
    display: flex;
    padding-bottom: 10px;
  }

  &.collapsing {
    visibility: hidden;
  }
}

.month-button {
  &[aria-expanded="true"] {
    color: #DADADA
  }
}

.fa-calendar-range {
  font-size: 22px;
}


</style>

<style lang="scss">
.month-selector {
  text-align: left;

  .btn {
    background: none;
    padding: 0;
    color: #000;
    font-weight: bold;

    &:hover {
      background: none; color: #000 !important ;
    }

    &:before {
      width: 0;
      height: 0;
      content: "";
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 10px solid #DADADA;
      display: inline-block;
      margin-right: 5px;
    }
  }
}

.month-drop {
  .badge.text-bg-secondary {
    background: #D9D9D9 !important;
    padding: 6px 15px; font-size: 12px;
    color: #000 !important;
  }
  .dropdown-menu {
    border-radius: 8px;
    box-shadow: rgba(0, 0, 0, 0.2) 4px 4px 4px;
    .badge { }
    .dropdown-item { padding: 10px 8px;  }
  }
}

.digi-booking {
  .nav-pills {
    border-radius: 6px;
    background: #D9D9D9;
    padding: 3px;

    .nav-link {
      color: #000 !important;
    }

    .active.bg-secondary {
      background: #fff !important;
      border-radius: 6px;
    }
  }

}

.d-times {
  padding-bottom: 20px !important;
}

// Hide sold out checkbox styles
.hide-sold-out {
  position: relative;
  cursor: pointer;
  user-select: none;
  .custom-checkbox-label {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid #D9D9D9;
    border-radius: 4px;
    background-color: #fff;
    i {
      color: v-bind(buttonText);
      font-size: 18px;
    }
  }
  // Add extra clickable area above and below div
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 0;
    right: 0;
    height: 4px;
    background: transparent;
  }
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    right: 0;
    height: 4px;
    background: transparent;
  }

  .custom-checkbox-label.checked {
    background-color: v-bind(buttonBG);
    border-color: v-bind(buttonBG);
  }

  span {
    color: #000 !important;
  }

}

.custom-hr { color: #626262; }

.no-sessions-message {
  padding: 12px;
  text-align: left;
  font-size: 16px;
  background-color: #FFF7E1;
  color: #000000;
  border-radius: 4px;
  border: #FED04D 1px solid;
  i { color: #FC8905; }
  span { font-weight: bold; }
}

.sessions-container { padding-bottom: 16px; }

</style>